import React, { useState } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';
import UserProfile from '../../../Component/Normal/UserProfile';

import DefaultLogoPath from '../../../image/DefaultLogo.png';

export default function PartnerGymInfoModal(props) {
  const { mode, data, onClose, onCancel, onSubmit } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const isApplication = mode === 'application';
  const isEditPermission = mode === 'edit-permission';

  // 모드별 모달 타이틀 키 결정 //
  let titleKey = 'myStoreSetting.partnerGym.requestModal.title';

  if (isEditPermission) titleKey = 'myStoreSetting.partnerGym.requestModal.titleEditPermission';
  else if (mode === 'pending-theirs') titleKey = 'myStoreSetting.partnerGym.requestModal.titleAccept';

  // 새로 권한을 정하는 모드(신규 신청 / 받은 신청 수락) — 디폴트: 매출 비공개, 예약 공개 //
  // 수정 모드 — 현재 내 row 의 권한값으로 초기화 //
  const isNewPermission = isApplication || mode === 'pending-theirs';

  const [isSalesShared, setIsSalesShared] = useState(isNewPermission ? false : (data?.isOpenSales ?? false));
  const [isReservationShared, setIsReservationShared] = useState(isNewPermission ? true : (data?.isOpenReservation ?? false));

  const salesLabel = isEditPermission ? t('myStoreSetting.partnerGym.requestModal.permissionSalesShare') : t('myStoreSetting.partnerGym.requestModal.permissionSales');
  const reservationLabel = isEditPermission ? t('myStoreSetting.partnerGym.requestModal.permissionReservationShare') : t('myStoreSetting.partnerGym.requestModal.permissionReservation');

  // 매출 공유 토글 클릭 //
  function onClickSalesToggle() {
    if (mode === 'pending-mine') {
      alert(t('myStoreSetting.alert.permissionPending'));
      return;
    }

    setIsSalesShared(!isSalesShared);
  }

  // 수업 예약 공유 토글 클릭 //
  function onClickReservationToggle() {
    if (mode === 'pending-mine') {
      alert(t('myStoreSetting.alert.permissionPending'));
      return;
    }

    setIsReservationShared(!isReservationShared);
  }

  // 수락하기 / 저장하기 클릭 //
  function onClickSubmit() {
    if (onSubmit) onSubmit({ isOpenSales: isSalesShared, isOpenReservation: isReservationShared });
  }

  // 권한 토글 한 줄 렌더 //
  function renderToggleRow(label, isOn, onToggle) {
    return (
      <ToggleRow>
        <ToggleLabel color={theme.text}>{label}</ToggleLabel>
        <ToggleRight>
          <SwitchTrack
            backgroundColor={isOn ? theme.primary : theme.subBackground}
            borderColor={isOn ? theme.primary : theme.line}
            onClick={onToggle}>
            <SwitchHandle
              isOn={isOn}
              handleColor={isOn ? theme.white : theme.line}
              borderColor={isOn ? theme.primary : theme.line} />
          </SwitchTrack>
          <ToggleStatus color={theme.text}>
            {isOn ? t('myStoreSetting.partnerGym.requestModal.enabled') : t('myStoreSetting.partnerGym.requestModal.disabled')}
          </ToggleStatus>
        </ToggleRight>
      </ToggleRow>
    );
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <HeaderRow>
          <HeaderTextBlock>
            <ModalTitle color={theme.text}>{t(titleKey)}</ModalTitle>
            <ModalDescription color={theme.subText}>{t('myStoreSetting.partnerGym.requestModal.description')}</ModalDescription>
          </HeaderTextBlock>
          <ExitButton onClick={onClose}>
            <ExitSvg color={theme.line} />
          </ExitButton>
        </HeaderRow>
        <ProfileRow>
          <ProfileLogo>
            <UserProfile profile={data?.storeLogo} defaultProfile={DefaultLogoPath} />
          </ProfileLogo>
          <ProfileTextBlock>
            <ProfileName color={theme.text}>{data?.storeName}</ProfileName>
            <ProfileAddress color={theme.text}>{data?.storeAddress}</ProfileAddress>
          </ProfileTextBlock>
        </ProfileRow>
        <DividerWrapper>
          <Divider backgroundColor={theme.line} />
        </DividerWrapper>
        {renderToggleRow(salesLabel, isSalesShared, onClickSalesToggle)}
        {renderToggleRow(reservationLabel, isReservationShared, onClickReservationToggle)}
        <ButtonRow>
          {
            mode === 'pending-mine' &&
            <PrimaryButton
              fullWidth
              backgroundColor={theme.primary}
              hoverBg={theme.primary}
              onClick={onCancel}>
              <PrimaryButtonText color={theme.subBackground}>{t('myStoreSetting.partnerGym.requestModal.cancelRequest')}</PrimaryButtonText>
            </PrimaryButton>
          }
          {
            mode === 'pending-theirs' &&
            <SecondaryButton
              backgroundColor={theme.buttonFill}
              hoverBg={theme.pointRed}
              onClick={onCancel}>
              <SecondaryButtonText color={theme.text}>{t('myStoreSetting.partnerGym.requestModal.reject')}</SecondaryButtonText>
            </SecondaryButton>
          }
          {
            mode === 'pending-theirs' &&
            <PrimaryButton
              backgroundColor={theme.primary}
              hoverBg={theme.primary}
              onClick={onClickSubmit}>
              <PrimaryButtonText color={theme.subBackground}>{t('myStoreSetting.partnerGym.requestModal.accept')}</PrimaryButtonText>
            </PrimaryButton>
          }
          {
            mode === 'edit-permission' &&
            <SecondaryButton
              backgroundColor={theme.buttonFill}
              onClick={onClose}>
              <SecondaryButtonText color={theme.text}>{t('myStoreSetting.partnerGym.requestModal.close')}</SecondaryButtonText>
            </SecondaryButton>
          }
          {
            mode === 'edit-permission' &&
            <SaveButton
              backgroundColor={theme.primary}
              hoverBg={theme.primary}
              onClick={onClickSubmit}>
              <PrimaryButtonText color={theme.subBackground}>{t('myStoreSetting.partnerGym.requestModal.save')}</PrimaryButtonText>
            </SaveButton>
          }
          {
            mode === 'application' &&
            <SecondaryButton
              backgroundColor={theme.buttonFill}
              onClick={onClose}>
              <SecondaryButtonText color={theme.text}>{t('myStoreSetting.partnerGym.requestModal.close')}</SecondaryButtonText>
            </SecondaryButton>
          }
          {
            mode === 'application' &&
            <SaveButton
              backgroundColor={theme.primary}
              hoverBg={theme.primary}
              onClick={onClickSubmit}>
              <PrimaryButtonText color={theme.subBackground}>{t('myStoreSetting.partnerGym.requestModal.apply')}</PrimaryButtonText>
            </SaveButton>
          }
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  padding: 20px 0;

  gap: 10px;

  width: 442px;

  border-radius: 10px;

  background-color: ${props => props.backgroundColor};

  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  padding: 0 20px;

  width: calc(100% - 40px);
`;

const HeaderTextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;
`;

const ModalTitle = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  width: 230px;

  color: ${props => props.color};
`;

const ModalDescription = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

// 닫기(X) 아이콘 — Figma(11434:105361) 벡터 직접 구현 //
function ExitSvg({ color }) {
  return (
    <svg width='16.5' height='15.93' viewBox='0 0 16.5001 15.9302' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M1.06066 14.7873C0.474874 14.2015 0.474874 13.2518 1.06066 12.666L12.666 1.06066C13.2518 0.474876 14.2015 0.474874 14.7873 1.06066C15.3731 1.64645 15.3731 2.59619 14.7873 3.18198L3.18198 14.7873C2.5962 15.3731 1.64645 15.3731 1.06066 14.7873Z'
        fill={color} />
      <rect
        x='2.77344'
        y='0.0822462'
        width='19.4124'
        height='3'
        rx='1.5'
        transform='rotate(45 2.77344 0.0822462)'
        fill={color} />
    </svg>
  );
}

const ProfileRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-start;

  gap: 21px;

  padding: 10px 20px;

  width: calc(100% - 40px);
`;

const ProfileLogo = styled.div`
  flex-shrink: 0;

  width: 55px;
  height: 55px;

  border-radius: 5px;

  overflow: hidden;
`;

const ProfileTextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  flex: 1;
  min-width: 0;

  gap: 7.5px;
`;

const ProfileName = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;
  line-height: 22px;

  margin: 0;

  color: ${props => props.color};
`;

const ProfileAddress = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 20px;

  margin: 0;

  max-width: 100%;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  color: ${props => props.color};
`;

const DividerWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 10px 20px;

  width: calc(100% - 40px);
`;

const Divider = styled.div`
  flex: 1;
  height: 1px;

  border-radius: 400px;

  background-color: ${props => props.backgroundColor};
`;

const ToggleRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  height: 51px;

  padding: 0 20px;

  width: calc(100% - 40px);
`;

const ToggleLabel = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const ToggleRight = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 140px;
`;

const SwitchTrack = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 70px;
  height: 32px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 8px;

  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SwitchHandle = styled.div`
  margin-left: ${props => props.isOn ? '42px' : '3px'};

  width: 25px;
  height: 25px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 8px;

  background-color: ${props => props.handleColor};

  transition: all 0.3s ease-in-out;
`;

const ToggleStatus = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;

  padding: 30px 20px 0;

  width: calc(100% - 40px);
`;

const PrimaryButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  ${props => props.fullWidth ? 'width: 100%;' : 'flex: 1 0 0;'}
  height: 40px;

  border-radius: 5px;

  background-color: ${props => props.backgroundColor};

  transition: background-color 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.hoverBg || props.backgroundColor};
    }
  }
`;

const SaveButton = styled(PrimaryButton)`
  flex: none;
  width: 279px;
`;

const SecondaryButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 40px;

  border-radius: 5px;

  background-color: ${props => props.backgroundColor};

  transition: background-color 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.hoverBg || props.backgroundColor};
    }
  }
`;

const PrimaryButtonText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const SecondaryButtonText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;
